/**
 * @file Transaction items (revenue) helper.
 */
import get from 'lodash.get';
import pluralize from 'pluralize';

import {
  TRANSACTION_ITEM_TYPE,
  PHOTO_ITEM_TYPE,
  SUBSCRIPTION_ITEM_TYPE,
  CUSTOM_PRINT_ITEM_TYPE,
  PURCHASE_TYPE_LABEL,
} from '../constants/transactions';
import {
  CUSTOM_PRINT_SUBTYPE,
  ORDER_STATUS,
} from '../constants/pro-custom-prints';
import { getAsset } from './assets';

/**
 * Returns the total refunded amount of a transaction item.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item to process.
 */
const getTransactionItemRefundedTotal = transactionItem =>
  (
    (transactionItem?.prepaid_transaction_item ?? transactionItem)
      ?.transaction_refunds?.records ?? []
  ).reduce((sum, refund) => sum + get(refund, 'amount', 0), 0);

/**
 * Returns the total refunded amount of a transaction based off their items.
 *
 * @param {Object<Transaction>} transaction The transaction item to process.
 */
const getTransactionRefundedTotal = transaction =>
  (transaction?.transaction_items?.records ?? []).reduce(
    (sum, transactionItem) =>
      sum + getTransactionItemRefundedTotal(transactionItem),
    0,
  );

/**
 * Returns the total prepaid amount of a transaction based off their items.
 *
 * @param {Object<Transaction>} transaction The transaction item to process.
 */
const getTransactionPrepaidTotal = transaction =>
  (transaction?.transaction_items?.records ?? []).reduce(
    (sum, transactionItem) => {
      if (!transactionItem?.prepaid_transaction_item) {
        return sum;
      }

      return (
        sum + transactionItem?.prepaid_transaction_item?.total_price?.value
      );
    },
    0,
  );

/**
 * Given a list of transaction items, returns the total amount spent.
 *
 * @param {Array} transactionItems List of transaction items to process.
 * @returns {Number} Total amount spent.
 */
const getTransactionItemsTotal = (transactionItems = []) =>
  transactionItems.reduce(
    (sum, transactionItem) =>
      sum +
      get(transactionItem, 'price.value', 0) -
      getTransactionItemRefundedTotal(transactionItem),
    0,
  );

/**
 * Given a list of transactions and type, will filter out
 * only those belonging to the type provided. Alternatively, subtype
 * can be passed down to filter.
 *
 * @param {Array} transactions List of transactions to process.
 * @param {enum<TRANSACTION_ITEM_TYPE>} itemType Type of the transaction items.
 * @param {enum<PHOTO_ITEM_TYPE> | enum<SUBSCRIPTION_ITEM_TYPE>} itemSubtype Subtype of the transaction items. Depends on the type.
 */
const getTransactionByType = (transactions = [], itemType, itemSubtype) =>
  transactions.filter(transaction => {
    const isValidItemType = Object.keys(TRANSACTION_ITEM_TYPE).includes(
      itemType,
    );
    const matchesItemType = transaction.item_type === itemType;

    if (!isValidItemType || !matchesItemType) {
      return false;
    }

    if (!itemSubtype) {
      return true;
    }

    const matchesSubtype =
      (itemType === TRANSACTION_ITEM_TYPE.PHOTO &&
        transaction.photo_item_type === itemSubtype) ||
      (itemType === TRANSACTION_ITEM_TYPE.SUBSCRIPTION &&
        transaction.subscription_item_type === itemSubtype);

    return matchesSubtype;
  });

/**
 * Returns the amount spent on Web Gallery Download items.
 *
 * @param {Array} transactions List of transactions to process.
 * @returns {Number} Total amount spent on Web Gallery Downloads.
 */
const getDownloadTransactionsTotal = transactions =>
  getTransactionItemsTotal(
    getTransactionByType(
      transactions,
      TRANSACTION_ITEM_TYPE.PHOTO,
      PHOTO_ITEM_TYPE.DOWNLOAD,
    ),
  );

/**
 * Returns the amount spent on Web Gallery Prints items.
 *
 * @param {Array} transactions List of transactions to process.
 * @returns {Number} Total amount spent on Web Gallery Prints.
 */
const getPrintTransactionsTotal = transactions =>
  getTransactionItemsTotal(
    getTransactionByType(
      transactions,
      TRANSACTION_ITEM_TYPE.PHOTO,
      PHOTO_ITEM_TYPE.PRINT,
    ),
  );

/**
 * Returns the amount spent on Print Package items.
 *
 * @param {Array} transactions List of transactions to process.
 * @returns {Number} Total amount spent on Print Packages.
 */
const getPrintPackageTransactionsTotal = transactions =>
  getTransactionItemsTotal(
    getTransactionByType(
      transactions,
      TRANSACTION_ITEM_TYPE.PHOTO,
      PHOTO_ITEM_TYPE.PRINT_PACKAGE,
    ),
  );

/**
 * Returns the amount spent on Photo Pricing items.
 *
 * @param {Array} transactions List of transactions to process.
 * @returns {Number} Total amount spent on Photo Pricing.
 */
const getPhotoPricingTransactionsTotal = transactions =>
  getTransactionItemsTotal(
    getTransactionByType(
      transactions,
      TRANSACTION_ITEM_TYPE.PHOTO,
      PHOTO_ITEM_TYPE.PHOTO_PRICING,
    ),
  );

/**
 * Returns the amount spent on Subscription items.
 *
 * @param {Array} transactions List of transactions to process.
 * @returns {Number} Total amount spent on Subscription.
 */
const getSubscriptionsTotal = transactions =>
  getTransactionItemsTotal(
    getTransactionByType(transactions, TRANSACTION_ITEM_TYPE.SUBSCRIPTION),
  );

/**
 * Given a photo item type, returns the label
 * to be presented to the user.
 *
 * @param {enum<PHOTO_ITEM_TYPE>} photoItemType Type to get label from.
 */
const getPhotoItemTypeLabel = photoItemType => {
  switch (photoItemType) {
    case PHOTO_ITEM_TYPE.DOWNLOAD:
      return 'Web Gallery Download';
    case PHOTO_ITEM_TYPE.PRINT:
      return 'Web Gallery Print';
    case PHOTO_ITEM_TYPE.PHOTO_PRICING:
      return 'Photo Pricing Item';
    case PHOTO_ITEM_TYPE.PRINT_PACKAGE:
      return 'Print Package';
    default:
      return '';
  }
};

/**
 * Given a subscription item type, returns the label
 * to be presented to the user.
 *
 * @param {enum<SUBSCRIPTION_ITEM_TYPE} subscriptionItemType Type to get label from.
 */
const getSubscriptionItemTypeLabel = subscriptionItemType => {
  switch (subscriptionItemType) {
    case SUBSCRIPTION_ITEM_TYPE.ALBUM:
      return 'Album Subscription';
    case SUBSCRIPTION_ITEM_TYPE.ALBUM_FOLDER:
      return 'Gallery Subscription';
    default:
      return '';
  }
};

/**
 * Returns the amount spent on Custom Print items.
 *
 * @param {Array} transactions List of transactions to process.
 * @returns {Number} Total amount spent on Photo Pricing.
 */
const getCustomPrintTransactionsTotal = transactions =>
  getTransactionItemsTotal(
    getTransactionByType(transactions, TRANSACTION_ITEM_TYPE.CUSTOM_PRINT),
  );

/**
 * Given a custom print item type, returns the label
 * to be presented to the user.
 *
 * @param {enum<CUSTOM_PRINT_ITEM_TYPE>} customPrintItemType Type to get label from.
 * @param {enum<CUSTOM_PRINT_ITEM_SUBTYPE>} customPrintSubtype Subtype to get label from.
 */
const getCustomPrintItemTypeLabel = (
  customPrintItemType,
  customPrintSubtype = null,
) => {
  if (customPrintSubtype === CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT) {
    return 'Specialty Product';
  }

  switch (customPrintItemType) {
    case CUSTOM_PRINT_ITEM_TYPE.ALBUM:
      return 'Album Custom Print';
    case CUSTOM_PRINT_ITEM_TYPE.ALBUM_FOLDER:
      return 'Gallery Custom Print';
    default:
      return '';
  }
};

/**
 * Returns the transaction item that should be targeted when issuing a refund.
 *
 * When a transaction item is pre-paid it has no price by itself, the price
 * (and therefore the refundable amount) lives in its `prepaid_transaction_item`.
 * In that case refunds must be issued against the pre-paid transaction item.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item to process.
 * @returns {Object<TransactionItem>} The transaction item to refund against.
 */
const getTransactionItemRefundTarget = transactionItem =>
  transactionItem?.prepaid_transaction_item ?? transactionItem;

/**
 * Returns the id of the transaction item that should be targeted when issuing a
 * refund, taking pre-paid transaction items into account.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item to process.
 * @returns {String} The id of the transaction item to refund against.
 */
const getTransactionItemRefundTargetId = transactionItem =>
  getTransactionItemRefundTarget(transactionItem)?.id;

/**
 * Returns the max refundable amount for a transaction item
 *
 * When the transaction item is pre-paid it has no price by itself, so the
 * refundable amount is taken from its `prepaid_transaction_item`.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item to process.
 */
const getTransactionItemMaxRefundableAmount = transactionItem => {
  const refundTarget = getTransactionItemRefundTarget(transactionItem);

  if (refundTarget?.__typename === 'PrepaidTransactionItem') {
    return (
      (refundTarget?.total_price?.value ?? 0) -
      (refundTarget?.transaction?.transaction_refunds?.records ?? []).reduce(
        (sum, refund) => sum + refund?.amount ?? 0,
        0,
      )
    );
  }

  return (
    (refundTarget?.total_price?.value ?? 0) -
    (refundTarget?.transaction_refunds?.records ?? []).reduce(
      (sum, refund) => sum + refund?.amount ?? 0,
      0,
    )
  );
};

/**
 * Returns the max refundable amount for a transaction
 *
 * @param {Object<Transaction>} transaction The transaction to process.
 */
const getTransactionMaxRefundableAmount = transaction => {
  const items = transaction?.transaction_items?.records ?? [];

  return items.reduce((sum, transactionItem) => {
    return sum + getTransactionItemMaxRefundableAmount(transactionItem);
  }, 0);
};

/**
 * Returns the CustomPrint a custom print transaction item was purchased from,
 * falling back to the one held by its purchased custom print.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item to process.
 * @returns {Object<CustomPrint>} The custom print, when the item has one.
 */
const getTransactionItemCustomPrint = transactionItem =>
  transactionItem?.custom_print ??
  transactionItem?.purchased_custom_print?.custom_print;

const getTransactionItemTypeLabel = transactionItem => {
  const itemType = transactionItem.item_type;
  const photoItemType = transactionItem.photo_item_type;
  const subscriptionItemType = transactionItem.subscription_item_type;
  const customPrintItemType = transactionItem.custom_print_item_type;
  const purchasedDigitalBundle = transactionItem.purchased_digital_bundle;

  switch (itemType) {
    case TRANSACTION_ITEM_TYPE.PHOTO: {
      return getPhotoItemTypeLabel(photoItemType);
    }
    case TRANSACTION_ITEM_TYPE.SUBSCRIPTION: {
      return getSubscriptionItemTypeLabel(subscriptionItemType);
    }
    case TRANSACTION_ITEM_TYPE.CUSTOM_PRINT: {
      return getCustomPrintItemTypeLabel(
        customPrintItemType,
        getTransactionItemCustomPrint(transactionItem)?.subtype,
      );
    }
    case TRANSACTION_ITEM_TYPE.DIGITAL_BUNDLE: {
      return `${pluralize(
        'Photo',
        purchasedDigitalBundle?.photos_count,
        true,
      )} Bundle`;
    }
    default: {
      return '';
    }
  }
};
/**
 * Given a transaction item, returns the name of the product that was actually
 * purchased (i.e. "5x7 Print", "Basic Package") rather than the type of the
 * item (i.e. "Web Gallery Print", "Print Package").
 *
 * Falls back to the type label for items that carry no product name.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item to process.
 * @returns {String} The purchased product name.
 */
const getTransactionItemProductName = transactionItem => {
  switch (transactionItem?.item_type) {
    case TRANSACTION_ITEM_TYPE.PHOTO: {
      if (transactionItem?.photo_print_size_name) {
        return `${transactionItem.photo_print_size_name} Print`;
      }

      return (
        transactionItem?.print_package_name ||
        transactionItem?.photo_pricing_name ||
        transactionItem?.print_product_configurations?.records?.[0]
          ?.print_product_configuration?.name ||
        getPhotoItemTypeLabel(transactionItem?.photo_item_type)
      );
    }
    case TRANSACTION_ITEM_TYPE.SUBSCRIPTION: {
      return getSubscriptionItemTypeLabel(
        transactionItem?.subscription_item_type,
      );
    }
    case TRANSACTION_ITEM_TYPE.CUSTOM_PRINT: {
      return (
        transactionItem?.custom_print_name ||
        transactionItem?.purchased_custom_print?.custom_print?.name ||
        getCustomPrintItemTypeLabel(
          transactionItem?.custom_print_item_type,
          transactionItem?.custom_print?.subtype,
        )
      );
    }
    case TRANSACTION_ITEM_TYPE.DIGITAL_BUNDLE: {
      return `${pluralize(
        'Photo',
        transactionItem?.purchased_digital_bundle?.photos_count,
        true,
      )} Bundle`;
    }
    case TRANSACTION_ITEM_TYPE.PRINT_ORDER_SERVICE: {
      return (
        transactionItem?.print_order_configuration?.print_partner_order_services?.records
          ?.map(
            ({ print_partner_order_service: service }) =>
              service?.print_order_service?.name,
          )
          .filter(Boolean)
          .join(', ') ?? ''
      );
    }
    default: {
      return '';
    }
  }
};

/**
 * Returns the print preview of a transaction item print product configuration.
 *
 * @param {Object<TransactionItemPrintProductConfiguration>} record The configuration record.
 * @returns {String|undefined} The url of the print preview.
 */
const getPrintProductConfigurationPreviewUrl = record =>
  record?.front_print_file_url ??
  record?.account_card?.front_preview_file_url ??
  undefined;

/**
 * Returns the individual products included in a purchased item, so a print
 * package can be listed by its contents instead of just its name.
 *
 * Contents are named after what the item recorded, while their preview is
 * taken from the print product configurations: a configuration names its own
 * preview, and an item holding a single print file has every one of its
 * contents printed on it. When an item holds several files there is no way to
 * tell which content each one belongs to, so those are left without a preview.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item to process.
 * @returns {Array<{quantity: Number, name: String, previewUrl: String}>} The included products.
 */
const getTransactionItemProductBreakdown = transactionItem => {
  const configurationRecords =
    transactionItem?.print_product_configurations?.records ?? [];
  const previewUrls = [
    ...new Set(
      configurationRecords
        .map(getPrintProductConfigurationPreviewUrl)
        .filter(Boolean),
    ),
  ];
  const sharedPreviewUrl =
    previewUrls.length === 1 ? previewUrls[0] : undefined;

  const printItems = (transactionItem?.print_items?.records ?? [])
    .filter(printItem => printItem?.print_package_item_type_name)
    .map(printItem => ({
      quantity: printItem?.quantity ?? 1,
      name: printItem.print_package_item_type_name,
      previewUrl: sharedPreviewUrl,
    }));

  // Packages made of configured products (i.e. ID cards) name their contents
  // after the custom print behind the configuration, mirroring the package
  // contents listed on the identity order management modal.
  const configurations = configurationRecords
    .map(record => ({
      quantity: record?.quantity ?? 1,
      name:
        record?.account_card?.album_custom_print?.custom_print?.name ??
        record?.print_product_configuration?.name,
      previewUrl: getPrintProductConfigurationPreviewUrl(record),
    }))
    .filter(({ name }) => !!name);

  // Nothing was recorded against the item itself, so fall back to what the
  // package is defined to include.
  const packageItems = (transactionItem?.print_package?.items?.records ?? [])
    .filter(item => !item?.is_optional)
    .map(item => ({
      quantity: item?.quantity ?? 1,
      name: item?.type?.name ?? item?.custom_print?.name,
      previewUrl: sharedPreviewUrl,
    }))
    .filter(({ name }) => !!name);

  const breakdown =
    [printItems, configurations, packageItems].find(
      items => items.length > 0,
    ) ?? [];

  if (transactionItem?.digital_downloads_purchase) {
    return [...breakdown, { quantity: 1, name: 'Digital Downloads' }];
  }

  return breakdown;
};

/**
 * Returns whether a purchased item gets its files delivered to the customer,
 * mirroring the "check your email for a link to download your pics" option the
 * checkout success screen offers. Album and gallery subscriptions are left out
 * on purpose: those grant access through the apps and the web gallery rather
 * than an emailed delivery.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item to process.
 * @returns {Boolean} Whether the item includes digitals.
 */
const transactionItemIncludesDigitals = transactionItem =>
  transactionItem?.item_type === TRANSACTION_ITEM_TYPE.DIGITAL_BUNDLE ||
  transactionItem?.photo_item_type === PHOTO_ITEM_TYPE.DOWNLOAD ||
  !!transactionItem?.digital_downloads_purchase;

/**
 * Returns the name to display for the customer behind a transaction item.
 *
 * Consumer accounts that never set up a name are stored as "Me", so items tied
 * to an identity are named after it, falling back to the shipping address name
 * and finally to the email address used for the order.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item to process.
 * @returns {String} The customer name.
 */
const getTransactionItemCustomerName = transactionItem => {
  const { transaction } = transactionItem ?? {};
  const accountName = transaction?.account?.name;
  const hasAccountName =
    !!accountName && accountName.trim().toLowerCase() !== 'me';

  return (
    transactionItem?.identity_name ||
    (hasAccountName ? accountName : '') ||
    transaction?.account_address?.name ||
    transaction?.email_address ||
    transaction?.account?.email_address ||
    ''
  );
};

/**
 * Fallback illustration per purchase type, mirroring the PurchasingOptions
 * option components, used when a print-config / photo preview isn't available.
 *
 * @param {Object<TransactionItem>} transactionItem The transaction item.
 */
const getTransactionItemFallbackIcon = transactionItem => {
  const itemType = transactionItem?.item_type;
  const photoItemType = transactionItem?.photo_item_type;

  if (itemType === TRANSACTION_ITEM_TYPE.CUSTOM_PRINT) {
    return getAsset('/plaque.svg');
  }
  if (
    itemType === TRANSACTION_ITEM_TYPE.DIGITAL_BUNDLE ||
    itemType === TRANSACTION_ITEM_TYPE.SUBSCRIPTION
  ) {
    return getAsset('/photopass.svg');
  }
  if (itemType === TRANSACTION_ITEM_TYPE.PHOTO) {
    if (photoItemType === PHOTO_ITEM_TYPE.DOWNLOAD) {
      return getAsset('/digital-download-illustration.svg');
    }
    if (photoItemType === PHOTO_ITEM_TYPE.PRINT_PACKAGE) {
      return getAsset('/icon-package-3d.svg');
    }
    return getAsset('/simple-prints-illustration.svg');
  }
  return getAsset('/icon-package-3d.svg');
};

const getTransactionItemTypeName = transactionItem => {
  if (transactionItem.print_package_name) {
    return transactionItem.print_package_name;
  }

  if (transactionItem.custom_print_name) {
    return transactionItem.custom_print_name;
  }

  return getTransactionItemTypeLabel(transactionItem);
};

const getTransactionPurchaseTypeLabel = transactionItem => {
  if (transactionItem?.is_prepaid_configuration) {
    return PURCHASE_TYPE_LABEL.CONFIGURATION;
  }

  if (transactionItem?.is_prepaid) {
    return PURCHASE_TYPE_LABEL.PRE_PAID;
  }

  return PURCHASE_TYPE_LABEL.STANDARD;
};

/**
 * Returns the items that can be refunded out of a transaction.
 *
 * @param {Object<Transaction>} transaction The transaction to process.
 */
const getTransactionRefundableItems = transaction => {
  const items = transaction?.transaction_items?.records ?? [];
  return items.filter(item => getTransactionItemMaxRefundableAmount(item) > 0);
};

/**
 * Returns the transaction items with a purchased custom print that has been fulfilled
 *
 * @param {Object<Transaction>} transaction The transaction to process.
 * @param {Boolean} fulfilledOnly whether to only get fulfilled items or not
 *
 * @returns {Array}
 */
const getSpecialtyPurchasedCustomPrintItems = (
  transaction,
  fulfilledOnly = true,
) => {
  return (transaction?.purchased_custom_prints?.records ?? []).filter(
    purchasedCustomPrint =>
      purchasedCustomPrint.custom_print?.subtype ===
        CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT &&
      (!fulfilledOnly ||
        purchasedCustomPrint.order_status === ORDER_STATUS.SHIPPED),
  );
};

const getTransactionItemStatus = item =>
  item?.purchased_photo_pricing?.status ??
  item?.purchased_print_package?.status ??
  item?.purchased_custom_print?.order_status;

const isPendingProcessingTransaction = transactionItems => {
  if (!transactionItems || !transactionItems?.length) {
    return false;
  }

  const pendingReorderStatuses = [
    ORDER_STATUS.PENDING_REVIEW,
    ORDER_STATUS.PENDING_BULK_SHIPPING,
  ];

  return (transactionItems ?? []).some(item =>
    pendingReorderStatuses.includes(getTransactionItemStatus(item)),
  );
};
export {
  getTransactionItemsTotal,
  getTransactionByType,
  getDownloadTransactionsTotal,
  getPrintTransactionsTotal,
  getPrintPackageTransactionsTotal,
  getPhotoPricingTransactionsTotal,
  getPhotoItemTypeLabel,
  getSubscriptionItemTypeLabel,
  getSubscriptionsTotal,
  getCustomPrintTransactionsTotal,
  getCustomPrintItemTypeLabel,
  getTransactionRefundedTotal,
  getTransactionPrepaidTotal,
  getTransactionItemRefundTarget,
  getTransactionItemRefundTargetId,
  getTransactionItemMaxRefundableAmount,
  getTransactionMaxRefundableAmount,
  getTransactionItemTypeLabel,
  getTransactionItemProductName,
  getTransactionItemProductBreakdown,
  transactionItemIncludesDigitals,
  getTransactionItemCustomerName,
  getTransactionPurchaseTypeLabel,
  getTransactionItemRefundedTotal,
  getTransactionRefundableItems,
  getTransactionItemTypeName,
  getTransactionItemFallbackIcon,
  getTransactionItemCustomPrint,
  getSpecialtyPurchasedCustomPrintItems,
  getTransactionItemStatus,
  isPendingProcessingTransaction,
};
